import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './EditProductModal.css';

const EditProductModal = ({ product, users, onClose, onSave }) => {
  const isCreateMode = !product;

  const [formData, setFormData] = useState({
    product_name: '',
    product_code: '',
    description: '',
    owner_id: '',
    categorization: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (product) {
      setFormData({
        product_name: product.product_name || '',
        product_code: product.product_code || '',
        description: product.description || '',
        owner_id: product.owner_id || '',
        categorization: product.categorization || '',
        status: product.status || 'active',
      });
    } else {
      setFormData({
        product_name: '',
        product_code: '',
        description: '',
        owner_id: '',
        categorization: '',
        status: 'active',
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.product_name.trim()) {
      setError('Product name is required');
      return;
    }
    if (formData.product_name.trim().length > 255) {
      setError('Product name must not exceed 255 characters');
      return;
    }
    if (formData.product_code && formData.product_code.length > 50) {
      setError('Product code must not exceed 50 characters');
      return;
    }

    setLoading(true);
    try {
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        if (value === '' && key !== 'product_name') return acc;
        acc[key] = value;
        return acc;
      }, {});

      await onSave(product?.id || null, cleanedData);
      setSuccess(isCreateMode ? 'Product created successfully!' : 'Product updated successfully!');
      setTimeout(() => { onClose(); }, 1000);
    } catch (err) {
      setError(err.message || (isCreateMode ? 'Failed to create product' : 'Failed to update product'));
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isCreateMode ? 'Create Product' : 'Edit Product'}</h2>
          <button className="modal-close" onClick={onClose} type="button" aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="modal-error">{error}</div>}
            {success && <div className="modal-success">{success}</div>}

            <div className="modal-field">
              <label htmlFor="product_name">Product Name <span className="required">*</span></label>
              <input
                type="text"
                id="product_name"
                name="product_name"
                value={formData.product_name}
                onChange={handleChange}
                maxLength={255}
                required
                placeholder="Enter product name"
                disabled={loading}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="product_code">Product Code</label>
              <input
                type="text"
                id="product_code"
                name="product_code"
                value={formData.product_code}
                onChange={handleChange}
                maxLength={50}
                placeholder="Enter product code (optional)"
                disabled={loading}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Enter product description (optional)"
                disabled={loading}
              />
            </div>

            <div className="modal-field">
              <label htmlFor="categorization">Categorization</label>
              <select
                id="categorization"
                name="categorization"
                value={formData.categorization}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">-- Select Categorization --</option>
                <option value="Hybrid Application">Hybrid Application</option>
                <option value="Cloud-Native Application">Cloud-Native Application</option>
                <option value="Hybrid Service">Hybrid Service</option>
                <option value="Cloud-Native Service">Cloud-Native Service</option>
                <option value="Hybrid Framework">Hybrid Framework</option>
                <option value="Cloud-Native Framework">Cloud-Native Framework</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="modal-field">
              <label htmlFor="owner_id">Owner</label>
              <select
                id="owner_id"
                name="owner_id"
                value={formData.owner_id}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="">-- Select Owner --</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-field">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="deprecated">Deprecated</option>
              </select>
            </div>

            {!isCreateMode && (
              <div className="modal-info">
                <strong>Note:</strong> Changing the product name will update it throughout the system. All associated requests and relationships will remain intact.
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="modal-button modal-button-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="modal-button modal-button-primary" disabled={loading}>
              {loading ? (
                <><span className="modal-spinner-small"></span>{isCreateMode ? 'Creating...' : 'Saving...'}</>
              ) : isCreateMode ? 'Create Product' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

EditProductModal.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    product_name: PropTypes.string,
    product_code: PropTypes.string,
    description: PropTypes.string,
    owner_id: PropTypes.string,
    categorization: PropTypes.string,
    status: PropTypes.string,
  }),
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditProductModal;
